import React, { useState, useEffect } from 'react';

export default function WhackAMole() {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(30);
  const [activeMole, setActiveMole] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [playerName, setPlayerName] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);

  const HOLES = 9;
  const GAME_DURATION = 30;

  // Timer del juego
  useEffect(() => {
    if (gameState === 'playing' && time > 0) {
      const timer = setInterval(() => setTime(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (gameState === 'playing' && time === 0) {
      finishGame();
    }
  }, [gameState, time]);

  // Aparecer topos aleatorios
  useEffect(() => {
    if (gameState === 'playing') {
      const interval = setInterval(() => {
        setActiveMole(Math.floor(Math.random() * HOLES));
      }, 600);
      return () => clearInterval(interval);
    }
  }, [gameState]);

  const playSound = (type) => {
    if (!soundEnabled) return;
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      if (type === 'hit') {
        oscillator.frequency.value = 600;
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      } else if (type === 'win') {
        oscillator.frequency.value = 1000;
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.5);
      }
    } catch (e) {
      console.log('Audio no disponible');
    }
  };

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setTime(GAME_DURATION);
    setActiveMole(null);
  };

  const hitMole = (index) => {
    if (activeMole === index && gameState === 'playing') {
      setScore(s => s + 1);
      playSound('hit');
      setActiveMole(null);
    }
  };

  const finishGame = () => {
    playSound('win');
    saveScore();
    setGameState('victory');
  };

  const saveScore = () => {
    const newScore = {
      name: playerName || 'Anónimo',
      score: score
    };
    const updated = [...ranking, newScore].sort((a, b) => b.score - a.score).slice(0, 10);
    setRanking(updated);
  };

  // Menú principal
  if (gameState === 'menu') {
    return (
      <div className="menu-container">
        <div className="menu-card">
          <h1 className="menu-title">🔨 Whack-a-Mole</h1>
          <p className="menu-subtitle">¡Golpea los topos lo más rápido que puedas!</p>

          <div className="menu-form">
            <div className="form-group">
              <label>Tu nombre:</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Ingresa tu nombre"
                className="input-text"
              />
            </div>
          </div>

          <button onClick={startGame} className="btn-play">
            Jugar (30 segundos)
          </button>

          {ranking.length > 0 && (
            <div className="ranking-section">
              <h2 className="ranking-title">🏆 Top 10 Ranking</h2>
              <div className="ranking-list">
                {ranking.map((score, idx) => (
                  <div key={idx} className="ranking-item">
                    <div className="ranking-left">
                      <span className="ranking-position">#{idx + 1}</span>
                      <span className="ranking-name">{score.name}</span>
                    </div>
                    <div className="ranking-right">
                      <span className="ranking-score-big">{score.score} topos</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Pantalla de juego
  if (gameState === 'playing') {
    return (
      <div className="game-container">
        <div className="game-wrapper">
          {/* Header */}
          <div className="game-header">
            <h1 className="game-title">🔨 Whack-a-Mole</h1>
            <div className="game-stats">
              <div className="stat-box">
                <span className="stat-label">⏱️</span>
                <span className="stat-value">{time}s</span>
              </div>
              <div className="stat-box">
                <span className="stat-label">🎯</span>
                <span className="stat-value">{score}</span>
              </div>
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className="btn-sound"
              >
                {soundEnabled ? '🔊' : '🔇'}
              </button>
            </div>
          </div>

          {/* Grid de topos */}
          <div className="moles-grid">
            {Array.from({ length: HOLES }).map((_, index) => (
              <div key={index} className="hole-container">
                <div className="hole">
                  <button
                    onClick={() => hitMole(index)}
                    className={`mole ${activeMole === index ? 'mole-active' : ''}`}
                  >
                    {activeMole === index ? '🐭' : ''}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Instrucciones */}
          <div className="instructions">
            <p>¡Golpea los topos que salgan antes de que desaparezcan!</p>
          </div>
        </div>
      </div>
    );
  }

  // Pantalla de victoria
  if (gameState === 'victory') {
    return (
      <div className="victory-container">
        <div className="victory-card">
          <div className="victory-emoji">🎉</div>
          <h1 className="victory-title">¡Tiempo!</h1>
          <p className="victory-subtitle">Se acabó el juego</p>

          <div className="victory-stats">
            <div className="stat-row">
              <span className="stat-label">Topos golpeados:</span>
              <span className="stat-value-large">{score}</span>
            </div>
          </div>

          <div className="victory-buttons">
            <button
              onClick={() => setGameState('menu')}
              className="btn-menu"
            >
              Volver al menú
            </button>
            <button
              onClick={startGame}
              className="btn-again"
            >
              🔄 Jugar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }
}
